<template>
  <loader :is-loading="clientLoading" />
  <div class="max-w-(--breakpoint-md) space-y-6">
    <lf-card v-if="!clientLoading" class="mb-8 py-6">
      <loader :is-loading="isSubmitting" />
      <div>
        <access-control :roles="[SUPER_ADMIN, ADMIN]">
          <div class="pt-2 pb-6" :class="{ 'border-b': !isAdmin }">
            <lf-switch
              v-model="selectedAllServices"
              name="data.stop_experian_dnb_services"
              @toggle-changed="handleServiceSelectAll"
            >
              <lf-h3>
                {{ $t("ORGANIZATION.INQUIRIES.TURN_ON_INQUIRIES") }}
              </lf-h3>
            </lf-switch>
          </div>
        </access-control>
        <div
          class="-mx-6 pr-6 space-y-4"
          :class="{
            'border-t border-b bg-gray-100 pt-6': isAdmin,
            'mt-4 px-6': !isAdmin,
          }"
        >
          <lf-h3 v-if="!isAdmin || isClientAdmin">
            {{ $t("ORGANIZATION.INQUIRIES.BOP_SERVICES_SUBSCRIBED") }}
          </lf-h3>
          <template
            v-if="
              (isAdmin || !client.stop_experian_dnb_services) &&
              !isFundingAdvisor
            "
          >
            <access-control :roles="[ADMIN, SUPER_ADMIN]">
              <div class="grid grid-cols-2" :class="{ 'mt-4': !isAdmin }">
                <div
                  v-for="(serviceName, serviceKey) in businessCreditServices"
                  :key="serviceKey"
                >
                  <lf-checkbox
                    v-model="selectedServices"
                    class="mb-4"
                    name="data.has_middesk"
                    :class="{ 'ml-8': isAdmin }"
                    :value="serviceKey"
                  >
                    {{ serviceName }}
                  </lf-checkbox>
                </div>
              </div>
            </access-control>
            <access-control
              :roles="[...CLIENT_ROLE_GROUP, FUNDING_ADVISOR, UNDERWRITER]"
            >
              <template v-if="Object.keys(selectedBopServices).length">
                <div
                  v-for="(serviceName, serviceKey) in selectedBopServices"
                  :key="serviceKey"
                >
                  <lf-h4 class="mt-4">
                    <span class="mr-2 text-checkmark-green">
                      <icon-base icon="success-round-big" />
                    </span>
                    {{ serviceName }}
                  </lf-h4>
                </div>
              </template>
              <div v-else>
                <business-credit-contact />
              </div>
            </access-control>
          </template>
          <template v-else>
            <div class="my-8">
              <business-credit-contact />
            </div>
          </template>
        </div>
        <access-control :roles="[ADMIN, SUPER_ADMIN]">
          <div
            class="flex min-w-full items-center justify-end pt-6 pr-5"
            :class="{
              'border-t': !Object.keys(selectedBopServices).length || !isAdmin,
            }"
          >
            <primary-button
              type="button"
              :disabled="isSubmitting"
              @click="submit"
            >
              {{ $t("COMMON.SAVE_CHANGES") }}
            </primary-button>
          </div>
        </access-control>
      </div>
    </lf-card>
    <experian-subcodes />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { useStore } from "vuex";

import {
  ADMIN,
  CLIENT_ROLE_GROUP,
  FUNDING_ADVISOR,
  LEGACY_SERVICES,
  SUPER_ADMIN,
  UNDERWRITER,
} from "@/helpers/constants";
import { useAuth } from "@/hooks/auth";
import { usePromiseWrapper } from "@/hooks/common";

import ExperianSubcodes from "@/components/profile/ExperianSubcodes.vue";
import LfCheckbox from "@/components/ui/inputs/LfCheckbox.vue";
import BusinessCreditContact from "@/views/profile/components/BusinessCreditContact.vue";

import type { EXPERIAN_BUSINESS_OWNER_PROFILE } from "@/helpers/constants";
import type { IClient } from "@/models/clients";
import type {
  BusinessCreditService,
  IBusinessCreditServices,
} from "@/models/options";

type BusinessCreditServices = Pick<
  IBusinessCreditServices,
  typeof EXPERIAN_BUSINESS_OWNER_PROFILE
>;

const emit = defineEmits<{
  (eventName: "updated"): void;
}>();
const { getters, dispatch } = useStore();
const { isFundingAdvisor, isAdmin, isUnderwriter, isClientAdmin } = useAuth();
const route = useRoute();
const selectedAllServices = ref(false);

const selectedBopServices = computed(() =>
  Object.fromEntries(
    Object.entries(businessCreditServices.value || {}).filter(([value]) =>
      selectedServices.value.includes(value as BusinessCreditService)
    )
  )
);

const client = computed<IClient>(() => ({
  ...getters[
    route.name === "ClientOrganization"
      ? "auth/authClientSettings"
      : "clients/active"
  ],
}));

const selectedServices = ref(client.value.business_credit_services || []); //has to be after client

const businessCreditServicesKeys = computed(
  () => Object.keys(businessCreditServices.value) as BusinessCreditService[]
);

const businessCreditServices = computed<BusinessCreditServices>(
  () => getters["options/experianBopBusinessCreditServices"]
);

onMounted(() => {
  selectedAllServices.value = businessCreditServicesKeys.value.every(
    (service: BusinessCreditService) => selectedServices.value.includes(service)
  );
});

const updateAction = computed(() =>
  route.name === "ClientOrganization"
    ? "auth/updateAuthClientSettings"
    : "clients/updateClient"
);

const clientLoading = computed<boolean>(
  () => getters["clients/clientsLoading"]
);

const handleServiceSelectAll = () => {
  const value = !selectedAllServices.value;
  selectedServices.value = value
    ? [
        ...new Set([
          ...selectedServices.value,
          ...businessCreditServicesKeys.value,
        ]),
      ]
    : selectedServices.value.filter(
        (service) => !businessCreditServicesKeys.value.includes(service)
      );
};

const { loading: isSubmitting, fetchWrapper: submit } = usePromiseWrapper(
  async () => {
    if (isUnderwriter || isFundingAdvisor) {
      return;
    }
    const businessCreditServices = [...selectedServices.value].filter(
      (service) => !LEGACY_SERVICES.includes(service)
    );
    const data = {
      business_credit_services: businessCreditServices,
    };
    const response = await dispatch(
      updateAction.value,
      route.name === "ClientOrganization" ? data : { id: client.value.id, data }
    );
    if (response.business_credit_services) {
      selectedServices.value = response.business_credit_services;
    }
    emit("updated");
  }
);

watch(selectedServices, () => {
  selectedAllServices.value = businessCreditServicesKeys.value.every(
    (service: BusinessCreditService) => selectedServices.value.includes(service)
  );
});

watch(
  () => client.value.business_credit_services,
  (val) => {
    selectedServices.value = val;
  }
);
</script>
